import type { AppInfo } from "@/shared/apps";
import { useCan } from "@/shared/lib/session";
import { menuGroups, useVisibleApps } from "./apps";

export interface Create { label: string; to: string }

/** The things people make most often, first: what the home page and "+ New" offer before the rest. */
const RANK = [
  "create.invoice", "create.payment", "create.quote", "create.expense", "create.lead", "create.organization", "create.request", "create.appointment",
  "create.salesOrder", "create.po", "create.ticket", "create.project", "create.employee", "create.product", "create.delivery", "create.entry",
  "create.receiptVoucher", "create.paymentVoucher", "create.moneyTransfer", "create.productionOrder", "create.saleContract",
];

/**
 * Creates in the order people reach for them; anything not ranked follows, as given. With
 * `here`, what belongs where the user is (a delivery note on the deliveries page) comes first.
 */
export function rankCreates<C extends { label: string; to: string }>(items: C[], here?: (c: C) => boolean): C[] {
  const at = (c: C) => (RANK.includes(c.label) ? RANK.indexOf(c.label) : RANK.length);
  const away = (c: C) => (here?.(c) ? 0 : 1);
  return items.map((c, i) => ({ c, i })).sort((a, b) => away(a.c) - away(b.c) || at(a.c) - at(b.c) || a.i - b.i).map((x) => x.c);
}

/** Everything the user may create in the apps that are on, under the menu's headings (label: an i18n key). */
export function useCreateGroups(): { label?: string; items: Create[] }[] {
  const can = useCan();
  const visible = useVisibleApps();
  const creates = (a: AppInfo) => (visible.find((v) => v.key === a.key)?.create ?? []).filter((c) => can(c.perm));
  return menuGroups.map((g) => ({ label: g.label, items: g.apps.flatMap(creates) })).filter((g) => g.items.length > 0);
}

/** Everything the user may create, most used first; with `here`, what belongs where the user is first of all. */
export function useCreates(here?: (c: Create) => boolean): Create[] {
  return rankCreates(useCreateGroups().flatMap((g) => g.items), here);
}
